import { useState, useEffect } from 'react';

// Extensão de tipos para a Network Information API (experimental, Chromium-only)
interface NetworkInformation {
  readonly type?: 'bluetooth' | 'cellular' | 'ethernet' | 'none' | 'other' | 'unknown' | 'wifi' | 'wimax';
  readonly effectiveType?: '2g' | '3g' | '4g' | 'slow-2g';
  readonly downlink?: number;
  addEventListener?(type: string, listener: EventListenerOrEventListenerObject): void;
  removeEventListener?(type: string, listener: EventListenerOrEventListenerObject): void;
}
declare global {
  interface Navigator { readonly connection?: NetworkInformation }
}

export interface NetworkHealth {
  pingMs:         number | null;
  bandwidth:      number | null;  // Mbps via navigator.connection.downlink
  connectionType: string | null;  // '4g' | '3g' | '2g' | 'slow-2g'
  isOnline:       boolean;
}

export function useNetworkHealth(): NetworkHealth {
  const [pingMs,         setPingMs]         = useState<number | null>(null);
  const [bandwidth,      setBandwidth]      = useState<number | null>(null);
  const [connectionType, setConnectionType] = useState<string | null>(null);
  const [isOnline,       setIsOnline]       = useState(navigator.onLine);

  useEffect(() => {
    let destroyed = false;

    const medir = async () => {
      try {
        const t0 = performance.now();
        await fetch('/', { method: 'HEAD', cache: 'no-store' });
        if (!destroyed) setPingMs(Math.round(performance.now() - t0));
      } catch {
        if (!destroyed) setPingMs(null);
      }
    };
    medir();
    const interval = setInterval(medir, 5_000);

    const conn = navigator.connection;
    const atualizarConexao = () => {
      setBandwidth(conn?.downlink ?? null);
      // type = tecnologia física (wifi, cellular, ethernet…); effectiveType = qualidade estimada (4g/3g/2g).
      // Preferir type para exibir a tecnologia real — effectiveType reporta '4g' em WiFi.
      setConnectionType(conn?.type ?? conn?.effectiveType ?? null);
    };
    atualizarConexao();
    conn?.addEventListener?.('change', atualizarConexao);

    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      destroyed = true;
      clearInterval(interval);
      conn?.removeEventListener?.('change', atualizarConexao);
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return { pingMs, bandwidth, connectionType, isOnline };
}
